import { Component, Inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormErrorStateMatcher, FormService } from 'src/app/core/form.service';
import {
  Approved,
  BDResult,
  Channel,
  ListOrdersParams,
  OrderStatus,
  Origin,
  TaxDeductionType,
  VbanStatus,
} from 'src/app/core/orders.service';
import { UtilsService } from 'src/app/core/utils.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { LastStatusChangeType } from 'src/app/shared/constants/operationals';
import { CompaniesService } from 'src/app/core/companies.service';
import { ErrorService } from 'src/app/core/error.service';

const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY', // this is how your date will be parsed from Input
  },
  display: {
    dateInput: 'DD/MM/YYYY', // this is how your date will get displayed on the Input
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-orders-filter-dialog',
  templateUrl: './orders-filter-dialog.component.html',
  styleUrls: ['./orders-filter-dialog.component.scss'],

  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
})
export class OrdersFilterDialogComponent {
  orders_filters_from_group: FormGroup = this.form_service.orders_filter_form_group;
  error_matcher: FormErrorStateMatcher = this.form_service.matcher;
  completed_status: OrderStatus = OrderStatus.completed;
  running_status: OrderStatus = OrderStatus.running;
  refunded_status: OrderStatus = OrderStatus.refunded;
  canceled_status: OrderStatus = OrderStatus.canceled;
  store_review_status: OrderStatus = OrderStatus.store_review;
  pending_status: OrderStatus = OrderStatus.pending;
  archived_status: OrderStatus = OrderStatus.archived;
  off_session_channel: Channel = Channel.off_session;
  on_session_channel: Channel = Channel.on_session;
  vban_pending_status: VbanStatus = VbanStatus.pending;
  vban_succeeded_status: VbanStatus = VbanStatus.succeeded;
  vban_partially_funded_status: VbanStatus = VbanStatus.partially_funded;
  liquidation_ok: string = 'ok';
  liquidation_ko: string = 'ko';
  del_pos: BDResult = BDResult.del_pos;
  del_neg: BDResult = BDResult.del_neg;
  web_origin: Origin = Origin.web;
  in_store_origin: Origin = Origin.in_store;
  bonus_mobili: TaxDeductionType = TaxDeductionType.bonus_mobili;
  conto_termico: TaxDeductionType = TaxDeductionType.conto_termico;
  conto_termico_3: TaxDeductionType = TaxDeductionType.conto_termico_3;
  interventi_sismici: TaxDeductionType = TaxDeductionType.interventi_sismici;
  riqualifica_edilizia: TaxDeductionType = TaxDeductionType.riqualifica_edilizia;
  risparmio_energetico: TaxDeductionType = TaxDeductionType.risparmio_energetico;
  yes: Approved = Approved.yes;
  no: Approved = Approved.no;
  lastOrderStatusOptions: Array<{ value: string; label: string }> = [];
  company_agreement_list: string[] = [];
  is_list_running: boolean = false;

  constructor(
    private dialog_ref: MatDialogRef<OrdersFilterDialogComponent>,
    private utils_service: UtilsService,
    private form_service: FormService,
    private companies_service: CompaniesService,
    private error_service: ErrorService,
    @Inject(MAT_DIALOG_DATA) data: any,
  ) {}

  ngOnInit(): void {
    this.lastOrderStatusOptions = this.getLastOrderStatusOptions();
    this.listCompanyAgreements();
  }

  onCloseClick(): void {
    this.dialog_ref.close();
  }

  onApplyFiltersClick(): void {
    if (this.isFormValid()) {
      let status: string = this.orders_filters_from_group.get(this.form_service.status_label)?.value;
      const order_id: string | '' = this.orders_filters_from_group.get(this.form_service.order_id_label)?.value;
      const company_id: string | '' = this.orders_filters_from_group.get(this.form_service.company_id_label)?.value;
      const buyer_name: string | '' = this.orders_filters_from_group.get(this.form_service.buyer_name_label)?.value;
      const channel: Channel | '' = this.orders_filters_from_group.get(this.form_service.channel_label)?.value;
      const origin: Origin | '' = this.orders_filters_from_group.get(this.form_service.origin_label)?.value;
      const created_from_text: string = this.orders_filters_from_group.get(this.form_service.created_from_label)?.value;
      const created_to_text: string = this.orders_filters_from_group.get(this.form_service.created_to_label)?.value;
      const created_to_date: Date = new Date(created_to_text);
      const created_to: number = created_to_date.setHours(23, 59, 59, 999);
      const created_from: number = new Date(created_from_text).getTime();
      const completed_from_text: string = this.orders_filters_from_group.get(
        this.form_service.completed_from_label,
      )?.value;
      const completed_from: number = new Date(completed_from_text).getTime();
      const completed_to_text: string = this.orders_filters_from_group.get(this.form_service.completed_to_label)?.value;
      const completed_to_date: Date = new Date(completed_to_text);
      const completed_to: number = completed_to_date.setHours(23, 59, 59, 999);
      const last_operation: string | '' = this.orders_filters_from_group.get(
        this.form_service.last_operation_label,
      )?.value;
      const last_operation_from_text: string = this.orders_filters_from_group.get(
        this.form_service.last_operation_from_label,
      )?.value;
      const last_operation_from: number = new Date(last_operation_from_text).getTime();
      const last_operation_to_text: string = this.orders_filters_from_group.get(
        this.form_service.last_operation_to_label,
      )?.value;
      const last_operation_to_date: Date = new Date(last_operation_to_text);
      const last_operation_to: number = last_operation_to_date.setHours(23, 59, 59, 999);
      const bd1: string | '' = this.orders_filters_from_group.get(this.form_service.bd1_label)?.value;
      const bd2: string | '' = this.orders_filters_from_group.get(this.form_service.bd2_label)?.value;
      const bd5: string | '' = this.orders_filters_from_group.get(this.form_service.bd5_label)?.value;
      const liquidation: string | '' = this.orders_filters_from_group.get(this.form_service.liquidation_label)?.value;
      const qinetic_id: string | '' = this.orders_filters_from_group.get(this.form_service.qinetic_id_label)?.value;
      const store_name: string | '' = this.orders_filters_from_group.get(this.form_service.store_name_label)?.value;
      const approved: string | '' = this.orders_filters_from_group.get(this.form_service.approved_label)?.value;
      const qinetic_bug: string | '' = this.orders_filters_from_group.get(this.form_service.qinetic_bug_label)?.value;
      const has_qinetic_id: string | '' = this.orders_filters_from_group.get(
        this.form_service.has_qinetic_id_label,
      )?.value;
      const external_id: string | '' = this.orders_filters_from_group.get(this.form_service.external_id_label)?.value;
      const phone_number: string | '' = this.orders_filters_from_group.get(this.form_service.phone_number_label)?.value;
      const owner_link: string | '' = this.orders_filters_from_group.get(this.form_service.owner_link)?.value;
      const tax_deduction: string | '' = this.orders_filters_from_group.get(
        this.form_service.tax_deduction_label,
      )?.value;
      const tax_deduction_type: TaxDeductionType | '' = this.orders_filters_from_group.get(
        this.form_service.tax_deduction_type_label,
      )?.value;
      const merchant_refund: string | '' = this.orders_filters_from_group.get(
        this.form_service.merchant_refund_label,
      )?.value;
      const agreement: string = this.orders_filters_from_group.get(this.form_service.agreement_label)?.value;
      const params: ListOrdersParams = {
        order_id: order_id == '' ? undefined : order_id,
        company_id: company_id == '' ? undefined : company_id,
        buyer_name: buyer_name == '' ? undefined : buyer_name,
        status: status == '' ? undefined : (status as OrderStatus),
        channel: channel == '' ? undefined : channel,
        origin: origin == '' ? undefined : origin,
        created_from: !created_from_text || created_from_text == '' ? undefined : created_from,
        created_to: !created_to_text || created_to_text == '' ? undefined : created_to,
        completed_from: !completed_from_text || completed_from_text == '' ? undefined : completed_from,
        completed_to: !completed_to_text || completed_to_text == '' ? undefined : completed_to,
        last_operation: last_operation == '' ? undefined : last_operation,
        last_operation_from:
          !last_operation_from_text || last_operation_from_text == '' ? undefined : last_operation_from,
        last_operation_to: !last_operation_to_text || last_operation_to_text == '' ? undefined : last_operation_to,
        bd1: bd1 == '' ? undefined : (bd1 as BDResult),
        bd2: bd2 == '' ? undefined : (bd2 as BDResult),
        bd5: bd5 == '' ? undefined : (bd5 as BDResult),
        liquidation: liquidation == '' ? undefined : liquidation,
        qinetic_id: qinetic_id == '' ? undefined : qinetic_id,
        store_name: store_name == '' ? undefined : store_name,
        approved: approved == '' ? undefined : approved,
        external_id: external_id == '' ? undefined : external_id,
        phone_number: phone_number == '' ? undefined : phone_number,
        owner_link: owner_link == '' ? undefined : owner_link,
        qinetic_bug: qinetic_bug == '' ? undefined : qinetic_bug,
        tax_deduction: tax_deduction == '' ? undefined : tax_deduction,
        tax_deduction_type: tax_deduction_type == '' ? undefined : tax_deduction_type,
        merchant_refund: merchant_refund == '' ? undefined : (merchant_refund as VbanStatus),
        has_qinetic_id: has_qinetic_id == '' ? undefined : has_qinetic_id,
        agreement: agreement == '' ? undefined : agreement,
      };
      this.dialog_ref.close({ params });
    }
  }

  onClearFiltersClick(): void {
    this.orders_filters_from_group.reset();
  }

  isFormValid(): boolean {
    return this.form_service.validateForm(this.orders_filters_from_group);
  }

  getChannelLabel(channel: Channel): string {
    return this.utils_service.getChannelLabel(channel);
  }

  getOriginLabel(origin: Origin): string {
    return this.utils_service.getOriginLabel(origin);
  }

  getTaxDeductionLabel(tax_deduction: TaxDeductionType): string {
    return this.utils_service.getTaxDeductionLabel(tax_deduction);
  }

  getOrderStatusLabel(status: OrderStatus): string {
    return this.utils_service.getOrderStatusLabel(status);
  }

  getVbanStatusLabel(status: VbanStatus): string {
    return this.utils_service.getVbanStatusLabel(status);
  }

  getBDLabel(bd: BDResult): string {
    return this.utils_service.getBDLabel(bd);
  }

  getLastOrderStatusOptions(): { value: string; label: string }[] {
    return Object.values(LastStatusChangeType).map(value => ({
      value,
      label: this.getLastOrderStatusChangeLabel(value),
    }));
  }

  getLastOrderStatusChangeLabel(last_status_change: string | undefined): string {
    return this.utils_service.getLastOrderStatusChange(last_status_change);
  }

  listCompanyAgreements(): void {
    this.is_list_running = true;
    this.companies_service.listCompanyAgreements().subscribe({
      next: response => {
        console.log(response);
        this.company_agreement_list = response.data.data;
        this.is_list_running = false;
      },
      error: error => {
        this.error_service.processGenericApiError(error);
        this.is_list_running = false;
      },
    });
  }
}
